/**
 * Default Glycemic Targets
 * Daily targets by diabetes profile type
 */

import type { DiabetesType, PrimaryGoal } from '@/types/user';
import type { DailyGlycemicTargets } from '@/types/diary';

/**
 * Metric colors for the budget dashboard
 */
export const METRIC_COLORS = {
  glycemicLoad: '#3B82F6',  // Blue
  avgGI: '#8B5CF6',         // Purple
  netCarbs: '#F59E0B',      // Orange
  fiber: '#22C55E',         // Green
  sugar: '#EF4444',         // Red
} as const;

/**
 * Default targets by diabetes profile
 */
export const DEFAULT_TARGETS: Record<string, DailyGlycemicTargets> = {
  none: {
    glycemicLoad: 100,
    avgGIScore: 55,
    netCarbsG: 150,
    fiberG: 30,
    sugarG: 50,
  },
  general_health: {
    glycemicLoad: 100,
    avgGIScore: 55,
    netCarbsG: 150,
    fiberG: 30,
    sugarG: 50,
  },
  prediabetes: {
    glycemicLoad: 80,
    avgGIScore: 50,
    netCarbsG: 120,
    fiberG: 30,
    sugarG: 35,
  },
  insulin_resistant: {
    glycemicLoad: 80,
    avgGIScore: 50,
    netCarbsG: 120,
    fiberG: 30,
    sugarG: 35,
  },
  type2: {
    glycemicLoad: 80,
    avgGIScore: 50,
    netCarbsG: 100,
    fiberG: 30,
    sugarG: 30,
  },
  type1: {
    glycemicLoad: 100,
    avgGIScore: 55,
    netCarbsG: 130,
    fiberG: 30,
    sugarG: 40,
  },
  gestational: {
    glycemicLoad: 70,
    avgGIScore: 45,
    netCarbsG: 100,
    fiberG: 28,
    sugarG: 25,
  },
} as const;

/**
 * Target presets for settings screen
 */
export const TARGET_PRESETS = [
  {
    id: 'low_gi',
    nameKey: 'diary.targets.presets.lowGI',
    targets: {
      glycemicLoad: 80,
      avgGIScore: 50,
      netCarbsG: 100,
      fiberG: 30,
      sugarG: 30,
    },
  },
  {
    id: 'moderate',
    nameKey: 'diary.targets.presets.moderate',
    targets: {
      glycemicLoad: 100,
      avgGIScore: 55,
      netCarbsG: 150,
      fiberG: 30,
      sugarG: 50,
    },
  },
  {
    id: 'custom',
    nameKey: 'diary.targets.presets.custom',
    targets: null, // User sets manually
  },
] as const;

/**
 * Get default targets for a user based on their diabetes profile
 */
export function getDefaultTargets(
  diabetesType?: DiabetesType,
  primaryGoal?: PrimaryGoal
): DailyGlycemicTargets {
  // Check diabetes type first
  if (diabetesType && DEFAULT_TARGETS[diabetesType]) {
    return { ...DEFAULT_TARGETS[diabetesType] };
  }

  // Fall back to primary goal
  if (primaryGoal && DEFAULT_TARGETS[primaryGoal]) {
    return { ...DEFAULT_TARGETS[primaryGoal] };
  }

  // Default
  return { ...DEFAULT_TARGETS.none };
}
